/**
 * Marks the brief window during an explicit logout in which the app has no session cookies but
 * has not yet navigated away.
 *
 * Why this exists: logout clears `_Id` / `accountId` and then redirects to the OC auth portal
 * logout URL. In between, the SPA is still mounted, so AuthGuard sees no session and issues
 * `window.location.replace(loginUrl)` from its render path. `window.location.replace()` does not
 * halt script execution, so both redirects run and the browser honours the last one — login wins,
 * OC still has a live SSO session, and the user is silently signed back in (PRJCBUGEN-65938).
 *
 * Scope is deliberately narrow:
 *
 * - Set at cookie-clear time, not at the start of logout. AuthGuard only misbehaves once the
 *   cookies are gone; before that `hasInsightSession()` is true and the guard is quiet. Setting it
 *   earlier would mean the marker had to outlive the logout and support-session API calls, which
 *   each allow 60s, and could lapse before the cookies were actually cleared.
 * - Held in memory, not in storage. The window closes when the browser leaves the page, so the
 *   document's lifetime is exactly the right scope. Nothing can leak into a later page load and
 *   suppress a legitimate login redirect, and there is no storage call to fail in private mode.
 *
 * The expiry is a safety net for the pathological case where the logout navigation never commits
 * (for example the user cancels it). It self-heals on the next route change, because AuthGuard
 * re-evaluates on navigation.
 *
 * Note for future work: if the CamSDk post-logout backstop is added, the marker will need to
 * survive the round trip to the auth portal and back, and will have to move to sessionStorage.
 */

// 0 = no logout in flight. Otherwise the Date.now() at which cookies were cleared.
let logoutStartedAt = 0;

// Far longer than the clear-to-navigate gap (milliseconds), short enough that an abandoned
// navigation cannot leave routes unguarded for long.
const LOGOUT_WINDOW_MS = 30000;

/** Mark a logout as in flight. Call immediately before clearing session cookies. */
export function beginLogout() {
  logoutStartedAt = Date.now();
}

/** Clear the marker. */
export function endLogout() {
  logoutStartedAt = 0;
}

/** True while an explicit logout is in flight and has not gone stale. */
export function isLoggingOut() {
  if (!logoutStartedAt) {
    return false;
  }
  if (Date.now() - logoutStartedAt > LOGOUT_WINDOW_MS) {
    endLogout();
    return false;
  }
  return true;
}
